/** Client-side bucketing of the chain-level daily series into chart points (§10). */
import type { ChainDailyPoint } from './api.js';
import { daysBetween, shiftDays, timeframeSpec, type Timeframe } from './timeframe.js';

const MES_ABBR = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

export type { Timeframe };

/**
 * Re-bucket a chain-level DAILY series into chart points (velas) according to
 * the timeframe: N days per candle (2s → 1 día, 1m → 3, 3m → 7, 6m → 14), one
 * candle per month (1a) or one per year (5a). See `lib/timeframe.ts`.
 *
 * Counts/revenue are summed; occ/ADR/RevPAR are recomputed from the summed
 * components (never averaged). `period` is the candle's INDEX inside the
 * window, so the same candle of the current and the prior-year window line up
 * for YoY/YTD even cuando el año previo es bisiesto; for the 5-year timeframe
 * it is the year itself (each year is its own point, no overlay).
 *
 * `windowFrom` is the first day of the window (`chartWindows().cur.from`, o el
 * `.prev.from` cuando se bucketea la ventana del año previo): las velas se
 * cortan desde ahí, no desde el primer día CON DATO, para que un hueco al
 * principio no corra todas las velas. Pass one window's points per call.
 */
export interface ChainBucket {
  period: string;
  label: string;
  occPct: number | null; // 0..1
  adrUsd: number | null;
  revparUsd: number | null;
  roomRevenueUsd: number;
  totalRevenueUsd: number;
  totalRevenueBs: number;
  diamond: number;
  house: number;
  comp: number;
}

interface Acc {
  order: number;
  period: string;
  label: string;
  diamond: number;
  house: number;
  comp: number;
  roomsSold: number;
  roomsAvailable: number;
  roomRevenueUsd: number;
  totalRevenueUsd: number;
  totalRevenueBs: number;
}

const diaMes = (iso: string) =>
  `${Number(iso.slice(8, 10))} ${MES_ABBR[Number(iso.slice(5, 7)) - 1]}`;

/** Etiqueta de una vela de N días: '5 Ago' si N=1, '5–11 Ago' / '30 Jul–5 Ago' si no. */
function labelDias(inicio: string, velaDias: number): string {
  if (velaDias <= 1) return diaMes(inicio);
  const fin = shiftDays(inicio, velaDias - 1);
  const mismoMes = inicio.slice(0, 7) === fin.slice(0, 7);
  return mismoMes
    ? `${Number(inicio.slice(8, 10))}–${diaMes(fin)}`
    : `${diaMes(inicio)}–${diaMes(fin)}`;
}

export function bucketChain(
  points: ChainDailyPoint[],
  tf: Timeframe,
  windowFrom: string,
): ChainBucket[] {
  const spec = timeframeSpec(tf);
  const groups = new Map<number, Acc>();
  for (const p of points) {
    const yyyy = Number(p.fecha.slice(0, 4));
    const mm = Number(p.fecha.slice(5, 7));
    let order: number;
    let period: string;
    let label: string;
    if (spec.grano === 'anio') {
      order = yyyy;
      period = label = String(yyyy); // una vela por año, sin overlay
    } else if (spec.grano === 'mes') {
      const desde = Number(windowFrom.slice(0, 4)) * 12 + Number(windowFrom.slice(5, 7)) - 1;
      order = yyyy * 12 + mm - 1 - desde;
      period = `b${order}`;
      label = `${MES_ABBR[mm - 1]} ${String(yyyy).slice(2)}`;
    } else {
      order = Math.floor(daysBetween(windowFrom, p.fecha) / spec.velaDias);
      period = `b${order}`;
      label = labelDias(shiftDays(windowFrom, order * spec.velaDias), spec.velaDias);
    }
    const g =
      groups.get(order) ??
      ({
        order,
        period,
        label,
        diamond: 0,
        house: 0,
        comp: 0,
        roomsSold: 0,
        roomsAvailable: 0,
        roomRevenueUsd: 0,
        totalRevenueUsd: 0,
        totalRevenueBs: 0,
      } as Acc);
    g.diamond += p.diamond;
    g.house += p.house;
    g.comp += p.comp;
    g.roomsSold += p.roomsSold;
    g.roomsAvailable += p.roomsAvailable;
    g.roomRevenueUsd += p.roomRevenueUsd ?? 0;
    g.totalRevenueUsd += p.totalRevenueUsd ?? 0;
    g.totalRevenueBs += p.totalRevenueBs;
    groups.set(order, g);
  }
  const r2 = (x: number) => Math.round(x * 100) / 100;
  return [...groups.values()]
    .sort((a, b) => a.order - b.order)
    .map((g) => ({
      period: g.period,
      label: g.label,
      occPct: g.roomsAvailable > 0 ? g.roomsSold / g.roomsAvailable : null,
      adrUsd: g.roomsSold > 0 ? r2(g.roomRevenueUsd / g.roomsSold) : null,
      revparUsd: g.roomsAvailable > 0 ? r2(g.roomRevenueUsd / g.roomsAvailable) : null,
      roomRevenueUsd: r2(g.roomRevenueUsd),
      totalRevenueUsd: r2(g.totalRevenueUsd),
      totalRevenueBs: r2(g.totalRevenueBs),
      diamond: g.diamond,
      house: g.house,
      comp: g.comp,
    }));
}
